import { buildUrl, useApi } from "../api";
import type { SiteInfo } from "../api";
import { BetreiberHinweis, Card, ErrorNote, Loading } from "../components";

/**
 * Impressum nach § 5 Digitale-Dienste-Gesetz.
 *
 * Die Angaben kommen aus der Umgebung des Servers, nicht aus dem Quelltext.
 * Fehlen Pflichtangaben, steht hier ein deutlicher Hinweis statt eines
 * lückenhaften Impressums — ein falsches Impressum wäre schlimmer als ein
 * erkennbar noch nicht ausgefülltes.
 */
export default function Impressum() {
  const { data, loading, error, reload } = useApi<SiteInfo>(buildUrl("/api/site-info"));

  if (error) return <ErrorNote message={error} onRetry={reload} />;
  if (loading || !data) return <Loading rows={6} />;

  return (
    <>
      <div className="page-head">
        <h1>Impressum</h1>
        <p>Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG).</p>
        <BetreiberHinweis betreiber={data.betreiber} hauptseite={data.hauptseite} />
      </div>

      {!data.vollstaendig ? (
        <div className="notice error" role="alert" style={{ marginBottom: "1rem" }}>
          <strong>Diese Seite ist noch nicht betriebsbereit.</strong> Die
          Betreiberangaben fehlen. Ohne vollständiges Impressum darf das Angebot
          nicht öffentlich erreichbar sein — ein fehlendes Impressum ist
          abmahnfähig. Die Angaben gehören als <code>SITE_*</code>-Werte in die{" "}
          <code>.env</code> des Servers, siehe <code>.env.example</code>.
        </div>
      ) : null}

      <Card title="Diensteanbieter">
        <dl className="meta" style={{ marginTop: 0 }}>
          <div>
            <dt>Name</dt>
            <dd>{data.betreiber || <span style={{ color: "var(--ink-3)" }}>—</span>}</dd>
          </div>
          <div>
            <dt>Anschrift</dt>
            <dd>
              {data.strasse ? (
                <>
                  {data.strasse}
                  <br />
                  {data.plz_ort}
                  <br />
                  {data.land}
                </>
              ) : (
                <span style={{ color: "var(--ink-3)" }}>—</span>
              )}
            </dd>
          </div>
          <div>
            <dt>E-Mail</dt>
            <dd>
              {data.email ? (
                <a href={`mailto:${data.email}`}>{data.email}</a>
              ) : (
                <span style={{ color: "var(--ink-3)" }}>—</span>
              )}
            </dd>
          </div>
          {data.telefon ? (
            <div>
              <dt>Telefon</dt>
              <dd>{data.telefon}</dd>
            </div>
          ) : null}
        </dl>

        {/* Bei juristischen Personen verlangt § 5 DDG zusätzlich die
            Vertretungsberechtigten und den Registereintrag. Bei natürlichen
            Personen bleiben diese Felder leer und der Block entfällt. */}
        {data.vertretung || data.registernummer || data.ustid ? (
          <dl className="meta" style={{ marginBottom: 0 }}>
            {data.vertretung ? (
              <div>
                <dt>Vertretungsberechtigt</dt>
                <dd>{data.vertretung}</dd>
              </div>
            ) : null}
            {data.registernummer ? (
              <div>
                <dt>Registereintrag</dt>
                <dd>
                  {data.registergericht ? `${data.registergericht}, ` : ""}
                  {data.registernummer}
                </dd>
              </div>
            ) : null}
            {data.ustid ? (
              <div>
                <dt>Umsatzsteuer-Identifikationsnummer</dt>
                <dd>
                  {data.ustid}
                  <br />
                  <span style={{ fontSize: "0.8rem", color: "var(--ink-3)" }}>
                    gemäß § 27 a Umsatzsteuergesetz
                  </span>
                </dd>
              </div>
            ) : null}
          </dl>
        ) : null}
      </Card>

      <Card
        title="Redaktionelle Verantwortung"
        subtitle="Nach § 18 Absatz 2 Medienstaatsvertrag"
      >
        <p style={{ marginTop: 0 }}>
          Verantwortlich für die redaktionellen Inhalte:
          <br />
          {data.verantwortlich || data.betreiber || "—"}
          {data.strasse ? (
            <>
              <br />
              {data.strasse}, {data.plz_ort}
            </>
          ) : null}
        </p>
        <p style={{ fontSize: "0.9rem", color: "var(--ink-2)", marginBottom: 0 }}>
          Dieses Angebot wertet Wahlprogramme und parlamentarisches Handeln aus und
          ordnet beides zueinander. Es dürfte damit journalistisch-redaktionell
          gestaltet sein, weshalb eine verantwortliche Person zu benennen ist.
        </p>
      </Card>

      <Card title="Haftung">
        <p style={{ marginTop: 0 }}>
          <strong>Inhalte.</strong> Die dargestellten Bewertungen sind
          Meinungsäußerungen auf Grundlage der jeweils genannten Belege, keine
          Tatsachenbehauptungen. Maßgeblich sind die verlinkten Originaldokumente.
          Für die Richtigkeit der von Dritten bezogenen Daten (Deutscher Bundestag,
          abgeordnetenwatch.de) wird keine Gewähr übernommen.
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>Links.</strong> Für die Inhalte verlinkter externer Seiten sind
          deren Betreiber verantwortlich. Zum Zeitpunkt der Verlinkung waren dort
          keine Rechtsverstöße erkennbar.
        </p>
      </Card>

      <Card title="Hinweise und Korrekturen">
        <p style={{ marginTop: 0, marginBottom: 0 }}>
          Wer eine Zuordnung oder Bewertung für unzutreffend hält, kann das unter der
          oben genannten Adresse mitteilen. Jede Bewertung nennt ihre Belege; eine
          Korrektur ersetzt die bisherige Einschätzung nicht, sondern tritt daneben,
          sodass die Änderung nachvollziehbar bleibt.
        </p>
      </Card>
    </>
  );
}
